import { BaseService } from './BaseService';
import type { AgentMemoryEntryDto, MemoryFilters, StoreMemoryRequest } from '../types/memory';

/**
 * Memory service for handling agent memory operations.
 */
export class MemoryService extends BaseService {
  /**
   * Gets memories with optional filtering and search.
   */
  async getMemories(filters?: MemoryFilters): Promise<AgentMemoryEntryDto[]> {
    const queryParams = new URLSearchParams();
    if (filters?.sessionId) queryParams.append('sessionId', filters.sessionId);
    if (filters?.scope !== undefined) queryParams.append('scope', filters.scope.toString());
    if (filters?.type !== undefined) queryParams.append('type', filters.type.toString());
    if (filters?.minImportanceScore !== undefined)
      queryParams.append('minImportanceScore', filters.minImportanceScore.toString());
    if (filters?.query) queryParams.append('query', filters.query);
    if (filters?.useSemanticSearch !== undefined)
      queryParams.append('useSemanticSearch', String(filters.useSemanticSearch));
    if (filters?.limit !== undefined) queryParams.append('limit', filters.limit.toString());
    if (filters?.includeProjectScope !== undefined)
      queryParams.append('includeProjectScope', String(filters.includeProjectScope));
    if (filters?.includeTenantScope !== undefined)
      queryParams.append('includeTenantScope', String(filters.includeTenantScope));

    const url = `/memories${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<AgentMemoryEntryDto[]>(url);
  }

  /**
   * Gets memories for a specific session.
   */
  async getSessionMemories(
    sessionId: string,
    filters?: Omit<MemoryFilters, 'sessionId'>
  ): Promise<AgentMemoryEntryDto[]> {
    const queryParams = new URLSearchParams();
    if (filters?.minImportanceScore !== undefined)
      queryParams.append('minImportanceScore', filters.minImportanceScore.toString());
    if (filters?.type !== undefined) queryParams.append('type', filters.type.toString());
    if (filters?.query) queryParams.append('query', filters.query);
    if (filters?.useSemanticSearch !== undefined)
      queryParams.append('useSemanticSearch', String(filters.useSemanticSearch));
    if (filters?.limit !== undefined) queryParams.append('limit', filters.limit.toString());

    const url = `/sessions/${sessionId}/memories${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<AgentMemoryEntryDto[]>(url);
  }

  /**
   * Gets a memory entry by ID.
   */
  async getMemory(id: string): Promise<AgentMemoryEntryDto> {
    return this.get<AgentMemoryEntryDto>(`/memories/${id}`);
  }

  /**
   * Stores a new memory entry.
   */
  async storeMemory(data: StoreMemoryRequest): Promise<AgentMemoryEntryDto> {
    return this.post<AgentMemoryEntryDto>('/memories', data);
  }

  /**
   * Deletes a memory entry.
   */
  async deleteMemory(id: string): Promise<void> {
    return this.delete<void>(`/memories/${id}`);
  }
}

export const memoryService = new MemoryService();

